import { useUserStore, UIConfig } from '../stores/userStore';

/**
 * Hook per accedere alle configurazioni UI dell'app
 * Fornisce valori di fallback per tutti i campi di configurazione
 */
export const useAppConfig = () => {
  const appConfig = useUserStore(state => state.appConfig);
  
  // Configurazioni di default per fallback
  const defaultConfig: UIConfig = {
    logoUrl: '/wishlist.png',
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    ctaColor: '#28a745',
    ctaTextColor: '#ffffff',
    appTitle: 'Wishlist',
    minFilter: 0,
    maxFilter: 1000
  };
  
  return {
    // Configurazioni UI con fallback
    logoUrl: appConfig?.logoUrl || defaultConfig.logoUrl,
    primaryColor: appConfig?.primaryColor || defaultConfig.primaryColor,
    secondaryColor: appConfig?.secondaryColor || defaultConfig.secondaryColor,
    ctaColor: appConfig?.ctaColor || defaultConfig.ctaColor,
    ctaTextColor: appConfig?.ctaTextColor || defaultConfig.ctaTextColor,
    appTitle: appConfig?.appTitle || defaultConfig.appTitle,
    favicon: appConfig?.favicon,
    backgroundImage: appConfig?.backgroundImage,
    fontFamily: appConfig?.fontFamily,
    minFilter: appConfig?.minFilter || defaultConfig.minFilter,
    maxFilter: appConfig?.maxFilter || defaultConfig.maxFilter,
    
    // Utility per verificare se la configurazione è caricata
    isConfigLoaded: appConfig !== null,
    
    // Configurazione completa (per debug/development)
    rawConfig: appConfig
  };
};

/**
 * Hook per accedere ai selettori di configurazione UI
 * Fornisce selettori specifici per campi comuni
 */
export const useAppConfigSelectors = () => {
  const config = useAppConfig();
  
  return {
    // Selettore per colori del tema
    getThemeColors: () => ({
      primary: config.primaryColor,
      secondary: config.secondaryColor,
      cta: config.ctaColor,
      ctaText: config.ctaTextColor
    }),
    
    // Selettore per informazioni app
    getAppInfo: () => ({
      title: config.appTitle,
      logo: config.logoUrl,
      favicon: config.favicon
    }),
    
    // Selettore per configurazioni filtri
    getFilterConfig: () => ({
      min: config.minFilter,
      max: config.maxFilter
    }),
    
    // Selettore per personalizzazioni avanzate
    getAdvancedConfig: () => ({
      backgroundImage: config.backgroundImage,
      fontFamily: config.fontFamily
    })
  };
};

/**
 * Hook per gestire le actions di configurazione UI
 * Fornisce metodi per aggiornare le configurazioni
 */
export const useAppConfigActions = () => {
  const { setAppConfig, resetAppConfig } = useUserStore(state => ({
    setAppConfig: state.setAppConfig,
    resetAppConfig: state.resetAppConfig
  }));
  
  return {
    // Setta la configurazione UI completa
    setAppConfig,
    
    // Reset della configurazione UI
    resetAppConfig,
    
    // Utility per aggiornare solo i colori del tema
    updateThemeColors: (colors: {
      primary?: string;
      secondary?: string;
      cta?: string;
      ctaText?: string;
    }) => {
      const currentConfig = useUserStore.getState().appConfig;
      if (currentConfig) {
        setAppConfig({
          ...currentConfig,
          primaryColor: colors.primary || currentConfig.primaryColor,
          secondaryColor: colors.secondary || currentConfig.secondaryColor,
          ctaColor: colors.cta || currentConfig.ctaColor,
          ctaTextColor: colors.ctaText || currentConfig.ctaTextColor
        });
      }
    },
    
    // Utility per aggiornare informazioni app
    updateAppInfo: (info: {
      title?: string;
      logo?: string;
      favicon?: string;
    }) => {
      const currentConfig = useUserStore.getState().appConfig;
      if (currentConfig) {
        setAppConfig({
          ...currentConfig,
          appTitle: info.title || currentConfig.appTitle,
          logoUrl: info.logo || currentConfig.logoUrl,
          favicon: info.favicon || currentConfig.favicon
        });
      }
    }
  };
}; 